import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { clearToken, get, post, readToken, setExpiryCallback, storeToken } from './api';
import type { User } from './types';

interface AuthContext {
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => void;
}

const Context = createContext<AuthContext | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const signOut = useCallback(() => {
    clearToken();
    setUser(null);
  }, []);

  // A 401 from any request drops the session here, so an expired token is
  // handled once rather than in every page.
  useEffect(() => { setExpiryCallback(signOut); }, [signOut]);

  useEffect(() => {
    if (!readToken()) {
      setLoading(false);
      return;
    }
    get<{ user: User }>('/auth/me')
      .then(({ user: u }) => setUser(u))
      .catch(() => clearToken())
      .finally(() => setLoading(false));
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const { token, user: u } = await post<{ token: string; user: User }>(
      '/auth/login', { email, password },
    );
    storeToken(token);
    setUser(u);
  }, []);

  const value = useMemo(
    () => ({ user, loading, signIn, signOut }),
    [user, loading, signIn, signOut],
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useAuth(): AuthContext {
  const context = useContext(Context);
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>.');
  return context;
}
